/**
 * UpsellDialog — Verkaufsstarker Plan-Wahl-Dialog für Trial-User.
 *
 * Wird gezeigt, wenn der User:
 *   - Eine 2. Akte erstellen möchte (Backend antwortet 402)
 *   - Eine eingeschränkte Funktion verwenden möchte
 *   - Aktiv vom Trial-Banner kommt
 *
 * Zeigt beide Pläne nebeneinander mit klaren CTA, 3-Monate-Bindungs-Hinweis
 * und Erfolgs-Aussagen. Bei Klick auf einen Plan → navigate("/billing").
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, X, Check, ArrowRight, Zap, ShieldCheck } from "lucide-react";

const PLANS = [
  {
    id: "flexibel",
    name: "Flexibel",
    price: 85,
    perCase: 75,
    headline: "Pay-per-Gutachten",
    blurb: "Perfekt zum Einstieg — du zahlst nur, wenn du tatsächlich Gutachten erstellst.",
    features: [
      "85 € Grundgebühr / Monat",
      "+ 75 € pro fertigem Gutachten",
      "Voller Funktionsumfang",
      "Mobile Erfassung, PDF-Export, KI-Texte",
    ],
    badge: null,
  },
  {
    id: "flatrate",
    name: "Flatrate",
    price: 850,
    perCase: 17,
    headline: "Beste Wahl für Vielnutzer",
    blurb: "Unbegrenzt viele Gutachten — ideal ab ca. 12 Aufträgen / Monat.",
    features: [
      "850 € Grundgebühr / Monat",
      "+ 17 € pro Gutachten",
      "Unbegrenzte Anzahl Gutachten",
      "Sprachdiktat, DAT, Backoffice-Workflow",
      "Priorisierter Support",
    ],
    badge: "Empfohlen",
  },
];

export default function UpsellDialog({ open, onClose, headline, subheadline, trigger = "generic" }) {
  const navigate = useNavigate();
  if (!open) return null;

  const goToPlan = (planId) => {
    onClose?.();
    navigate(`/billing?plan=${planId}&from=${trigger}`);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="upsell-dialog">
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="brand-gradient-bg text-white px-6 py-7 sm:px-10 relative overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
            data-testid="upsell-close"
            aria-label="Schließen"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold opacity-90">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Plan freischalten · Easy Gutachter</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 leading-tight max-w-2xl">
            {headline || "Bereit, dein Gutachter-Business zu skalieren?"}
          </h2>
          <p className="mt-2 text-sm sm:text-base text-white/90 max-w-2xl">
            {subheadline || "Wähle deinen Plan und nutze Easy Gutachter sofort vollständig — alle Module, unbegrenzt viele Akten, KI-gestützte Texte, mobile App."}
          </p>
        </div>

        {/* Plans */}
        <div className="grid sm:grid-cols-2 gap-4 p-6 sm:p-8 bg-gradient-to-br from-zinc-50 via-white to-white">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border-2 p-5 sm:p-6 bg-white transition hover:shadow-lg ${
                plan.badge ? "border-cyan-400 shadow-md" : "border-zinc-200"
              }`}
              data-testid={`upsell-plan-${plan.id}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 right-5 bg-cyan-500 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full">
                  {plan.badge}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">{plan.headline}</div>
              <div className="font-display text-2xl font-bold mt-1">{plan.name}</div>
              <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{plan.blurb}</div>

              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="font-display text-4xl font-bold tabular-nums">{plan.price}</span>
                <span className="text-xl text-zinc-500">€</span>
                <span className="text-xs text-zinc-500 ml-1">/ Monat</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">+ {plan.perCase} € pro Gutachten</div>

              <ul className="space-y-1.5 mt-4 text-sm">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-600 mt-1 shrink-0" strokeWidth={2.4} />
                    <span className="text-zinc-700 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => goToPlan(plan.id)}
                className={`w-full mt-5 ${plan.badge ? "brand-gradient-bg text-white" : ""}`}
                variant={plan.badge ? "default" : "outline"}
                data-testid={`upsell-cta-${plan.id}`}
              >
                {plan.name} aktivieren
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 pb-6 pt-2 text-center">
          <div className="inline-flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> SEPA-Lastschrift — keine Kreditkarte</span>
            <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-amber-500" /> Sofortige Freischaltung</span>
            <span className="flex items-center gap-1.5">3 Monate Mindestlaufzeit · auto Verlängerung</span>
          </div>
        </div>
      </div>
    </div>
  );
}
